/**
 * Pure-utility helpers shared across TypographyV2 sub-components.
 * These replace the MUI `sx` / `variant` override system with
 * plain Tailwind className merging.
 */

import {
  VARIANT_CLASS_MAP,
  RESPONSIVE_VARIANT_CLASS_MAP,
  EXPLICIT_MOBILE_OVERRIDES,
} from './variantMap'

/**
 * Resolve the variant className(s) for a given block type.
 *
 * @param {string} blockType - The Contentful rich-text node type (e.g. 'paragraph')
 * @param {Object|undefined} variantOverride - Optional override map keyed by block type
 * @param {Object} richText - The block→variant mapping (default: RICH_TEXT)
 * @returns {string} The Tailwind class for the variant
 */
export function resolveVariantClass(blockType, variantOverride, richText) {
  const variant =
    typeof variantOverride === 'object' && Object.hasOwnProperty.call(variantOverride, blockType)
      ? variantOverride[blockType]
      : richText?.[blockType]?.variant

  return VARIANT_CLASS_MAP[variant] ?? ''
}

/**
 * Resolve responsive variant classNames (mobile + desktop).
 *
 * @param {string} blockType
 * @param {string|undefined} mobileVariant
 * @param {Object|undefined} mobileVariantOverride
 * @param {Object|undefined} variantOverride
 * @param {Object} richText
 * @returns {string} Combined Tailwind classes for mobile + lg: desktop
 */
export function resolveResponsiveVariantClass(
  blockType,
  mobileVariant,
  mobileVariantOverride,
  variantOverride,
  richText
) {
  // Mobile variant
  const mobile = EXPLICIT_MOBILE_OVERRIDES.includes(mobileVariant)
    ? mobileVariant
    : typeof mobileVariantOverride === 'object' &&
      Object.hasOwnProperty.call(mobileVariantOverride, blockType)
    ? mobileVariantOverride[blockType]
    : richText?.[blockType]?.variant

  // Desktop variant
  const desktop =
    typeof variantOverride === 'object' && Object.hasOwnProperty.call(variantOverride, blockType)
      ? variantOverride[blockType]
      : richText?.[blockType]?.variant

  const mobileClass = VARIANT_CLASS_MAP[mobile] ?? ''
  const desktopClass = RESPONSIVE_VARIANT_CLASS_MAP[desktop] ?? ''

  return `${mobileClass} ${desktopClass}`.trim()
}

/**
 * Resolve a className override string for a given block type.
 * Replaces MUI `sx` overrides — consumers now pass Tailwind className strings.
 *
 * @param {string} blockType
 * @param {Object|undefined} classNameOverride - Map of blockType → Tailwind class string
 * @returns {string}
 */
export function resolveClassNameOverride(blockType, classNameOverride) {
  if (
    typeof classNameOverride === 'object' &&
    Object.hasOwnProperty.call(classNameOverride, blockType)
  ) {
    return classNameOverride[blockType]
  }
  return ''
}
